import React, { CSSProperties, forwardRef, Ref, useCallback, useMemo, useState } from 'react';
import { IconButton, Button, Stack, Box, Menu, MenuItem, useMediaQuery, Divider, useTheme, IconButtonProps } from '@mui/material';
import { Tooltip } from 'components/elements';
import LogoutIcon from '@mui/icons-material/Logout';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import InviteSendArror from 'assets/icons/svg/sentArrow.svg?react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  LockOpenOutlined,
  CopyAllOutlined,
  TuneOutlined as TuneIcon,
  FileCopyOutlined as FileCopyIcon,
  ChevronRightOutlined as RightIcon,
  UploadFileOutlined as UploadFileIcon,
  Verified,
  VerifiedOutlined as NotVerifiedIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import RightArrow from 'assets/icons/svg/Right-Chevrolet.svg?react';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import { FormattedMessage } from 'react-intl';
import RefreshIcon from '@mui/icons-material/Refresh';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import SyncLockIcon from '@mui/icons-material/SyncLock';
import SyncIcon from '@mui/icons-material/Sync';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import { ReviewDocumentStatusType, ReviewDocumentStatusTypes } from 'types/prediction';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import { LinearProgress } from '@mui/material';
import { Fade } from '@mui/material';
import { Typography } from '@mui/material';
import { VerifiationStatusTypes } from 'types/verification';
import { filter } from 'lodash';
import ExportDropdown from 'components/buttons/ExportDropdown';
interface ActionButtonConfig {
  show: boolean;
  onClick: ((e?: any) => void) | undefined;
  tooltip?: string;
  disabled?: boolean;
  className?: string;
  text?: string; // New: Optional text for mobile menu
  color?: IconButtonProps['color'];
  type?: any;
}

interface TaggingConfig extends ActionButtonConfig {
  icon?: React.ReactNode;
  variant?: 'text' | 'outlined' | 'contained';
}
interface CompositeActionConfig {
  show: boolean;
  predictionReadyconfig: ActionButtonConfig & {
    status: ReviewDocumentStatusTypes;
    showProgress?: boolean;
    toggleAnim?: any;
    value?: number;
  };
  exportConfig?: Omit<ActionButtonConfig, 'onClick'> & {
    onDownload: (type: any) => void;
  };
}

interface ActionButtonsProps {
  tuneConfig?: ActionButtonConfig;
  nextConfig?: ActionButtonConfig;
  copyConfig?: ActionButtonConfig;
  editConfig?: ActionButtonConfig;
  exceptionConfig?: ActionButtonConfig;
  exceptionConfigAssurance?: ActionButtonConfig;
  readyForTrainingConfig?: ActionButtonConfig;
  trainingIncompleteConfig?: ActionButtonConfig;
  trainingStoppedConfig?: ActionButtonConfig;
  deleteConfig?: ActionButtonConfig;
  uploadFileConfig?: ActionButtonConfig;
  dpcConfig?: ActionButtonConfig;
  predictionConfig?: ActionButtonConfig;
  tagFileConfig?: ActionButtonConfig;
  lockResetConfig?: ActionButtonConfig & { isLocked?: boolean };
  passwordResetConfig?: { show: boolean; onClick: () => void; tooltip?: string; disabled?: boolean; className?: string };
  inviteSendConfig?: { show: boolean; onClick: () => void; tooltip?: string; disabled?: boolean; className?: string };
  passwordCopyConfig?: ActionButtonConfig;
  iconOrder?: string[];
  verifiedConfig?: ActionButtonConfig;
  notVerifiedConfig?: ActionButtonConfig;
  documentReadyConfig?: ActionButtonConfig & {
    status: ReviewDocumentStatusTypes;
    showProgress?: boolean;
    toggleAnim?: any;
    value?: number;
  };
  predictionReadyconfig?: ActionButtonConfig & {
    status: ReviewDocumentStatusTypes;
    showProgress?: boolean;
    toggleAnim?: any;
    value?: number;
  };
  tagging?: TaggingConfig;
  containerJustifyContent?: string;
  designStudio?: ActionButtonConfig;
  refreshConfig?: ActionButtonConfig;
  resetConfig?: ActionButtonConfig;
  clearConfig?: ActionButtonConfig;
  containerDisplayProperty?: CSSProperties['display'];
  rightArrowConfig?: ActionButtonConfig;
  viewConfig?: ActionButtonConfig;
  lockConfig?: ActionButtonConfig;
  batchPredictionExportConfig?: CompositeActionConfig;
  exportConfig?: Omit<ActionButtonConfig, 'onClick'> & {
    onDownload: (type: any) => void;
    disabled?: boolean;
  };
}

const documentReadyButtonLangKeys = {
  [ReviewDocumentStatusType.READY]: 'elements.buttons.ready',
  [ReviewDocumentStatusType.UPLOADED]: 'pages.review.prediction.labels.uploaded',
  [ReviewDocumentStatusType.PROCESSING]: 'pages.review.prediction.labels.processing',
  [ReviewDocumentStatusType.QAINPROGRESS]: 'pages.review.prediction.labels.qaInProgress',
  [ReviewDocumentStatusType.INTERRUPTED]: 'pages.review.prediction.labels.interrupted',
  [ReviewDocumentStatusType.INPROGRESS]: 'pages.review.prediction.labels.inProgress',
  [ReviewDocumentStatusType.COMPLETED]: 'pages.review.prediction.labels.completed',
  [ReviewDocumentStatusType.VERIFIED]: 'pages.review.prediction.labels.verified',
  [ReviewDocumentStatusType.EXPORTED]: 'pages.review.prediction.labels.exported',
  [ReviewDocumentStatusType.CURATIONINPROGRESS]: 'pages.review.prediction.labels.curationInProgress',
  [ReviewDocumentStatusType.CURATIONINCOMPLETE]: 'pages.review.prediction.labels.curationInComplete',
  [ReviewDocumentStatusType['API-INTERRUPTED']]: 'pages.review.prediction.labels.apiInterrupted',
  [ReviewDocumentStatusType.AUTOQC]: 'pages.review.prediction.labels.autoQC',
  [ReviewDocumentStatusType.YETTOSTART]: 'pages.review.prediction.labels.yetToStart',
  [ReviewDocumentStatusType.READYFORQC]: 'elements.buttons.readyForQc',
  [ReviewDocumentStatusType.QCINPROGRESS]: 'pages.review.prediction.labels.qcInProgress',
  [ReviewDocumentStatusType.QCINCOMPLETE]: 'pages.review.prediction.labels.qcIncomplete',
  [ReviewDocumentStatusType.QCVERIFIED]: 'pages.review.prediction.labels.qcVerified',
  [ReviewDocumentStatusType.EXCEPTION]: 'pages.review.prediction.labels.exception',
  [ReviewDocumentStatusType.INITIATED]: 'pages.review.prediction.labels.initiated',
  [ReviewDocumentStatusType.INQUEUE]: 'pages.review.prediction.labels.inQueue',
  [ReviewDocumentStatusType.VOLUMEEXCEEDED]: 'pages.review.prediction.labels.volumeExceeded',
  [ReviewDocumentStatusType.LOWDISKSPACE]: 'pages.review.prediction.labels.lowDiskSpace',
  [VerifiationStatusTypes.READY_FOR_TRAINING]: 'pages.review.verification.status.readyForTraining',
  [VerifiationStatusTypes.CURATION_COMPLETED]: 'pages.review.verification.status.curationCompleted',
  [VerifiationStatusTypes.READYFORCURATION]: 'pages.review.verification.status.readyForCuration',
  [ReviewDocumentStatusType.QCINTERRUPTED]: 'pages.review.prediction.labels.qcInterrupted'
};
const translate = (key: string, values?: any) => (window as any).translate(key, values);
const ActionButtons = forwardRef(
  (
    {
      tuneConfig,
      nextConfig,
      copyConfig,
      editConfig,
      exceptionConfig,
      exceptionConfigAssurance,
      readyForTrainingConfig,
      trainingIncompleteConfig,
      trainingStoppedConfig,
      deleteConfig,
      uploadFileConfig,
      dpcConfig,
      predictionConfig,
      tagFileConfig,
      lockResetConfig,
      passwordResetConfig,
      inviteSendConfig,
      passwordCopyConfig,
      verifiedConfig,
      notVerifiedConfig,
      documentReadyConfig,
      tagging,
      iconOrder = [
        'tuneConfig',
        'tagging',
        'copyConfig',
        'editConfig',
        'deleteConfig',
        'readyForTrainingConfig',
        'trainingIncompleteConfig',
        'trainingStoppedConfig',
        'uploadFileConfig',
        'dpcConfig',
        'predictionConfig',
        'tagFileConfig',
        'lockResetConfig',
        'nextConfig',
        'passwordResetConfig',
        'inviteSendConfig',
        'passwordCopyConfig',
        'verifiedConfig',
        'notVerifiedConfig',
        'designStudio',
        'refreshConfig',
        'resetConfig',
        'clearConfig',
        'rightArrowConfig',
        'viewConfig',
        'lockConfig',
        'batchPredictionExportConfig',
        'exportConfig'
      ],
      containerJustifyContent = 'center',
      designStudio,
      refreshConfig,
      resetConfig,
      clearConfig,
      containerDisplayProperty = 'flex',
      rightArrowConfig,
      viewConfig,
      lockConfig,
      batchPredictionExportConfig,
      exportConfig
    }: ActionButtonsProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const theme = useTheme();
    const [isPasswordCopied, setIsPasswordCopied] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const isMobile = useMediaQuery('(max-width:600px)');

    const _onPasswordCopy = useCallback(() => {
      if (passwordCopyConfig?.onClick) {
        passwordCopyConfig.onClick();
      }
      setIsPasswordCopied(true);
      setTimeout(() => setIsPasswordCopied(false), 3000);
    }, [passwordCopyConfig]);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const buttonConfigs: { [key: string]: (ActionButtonConfig & { icon: React.ReactNode; defaultTooltip?: string }) | undefined } = {
      tuneConfig: tuneConfig ? { ...tuneConfig, icon: <TuneIcon />, defaultTooltip: 'Tune' } : undefined,
      copyConfig: copyConfig ? { ...copyConfig, icon: <FileCopyIcon />, defaultTooltip: 'Copy' } : undefined,
      editConfig: editConfig ? { ...editConfig, icon: <EditOutlined />, defaultTooltip: 'Edit' } : undefined,
      exceptionConfig: exceptionConfig
        ? {
            ...exceptionConfig,

            icon: <AddCommentOutlinedIcon className={`add-comment-icon ${exceptionConfig.disabled ? 'disabled' : ''}`} />,
            defaultTooltip: `${translate('pages.exception.labels.toolTipComments')}`
          }
        : undefined,
      readyForTrainingConfig: readyForTrainingConfig
        ? {
            ...readyForTrainingConfig,

            icon: <PlayCircleOutlineOutlinedIcon />,
            defaultTooltip: `go`
          }
        : undefined,
      trainingIncompleteConfig: trainingIncompleteConfig
        ? {
            ...trainingIncompleteConfig,

            icon: <AutorenewOutlinedIcon />,
            defaultTooltip: `go`
          }
        : undefined,
      trainingStoppedConfig: trainingStoppedConfig
        ? {
            ...trainingStoppedConfig,

            icon: <AutorenewOutlinedIcon />,
            defaultTooltip: `go`
          }
        : undefined,
      exceptionConfigAssurance: exceptionConfigAssurance
        ? {
            ...exceptionConfigAssurance,
            icon: <LogoutIcon className={`add-comment-icon ${exceptionConfigAssurance.disabled ? 'disabled' : ''}`} />,
            defaultTooltip: `${translate('pages.exception.labels.toolTipAssurance')}`
          }
        : undefined,
      deleteConfig: deleteConfig ? { ...deleteConfig, icon: <DeleteOutlined />, defaultTooltip: 'Delete' } : undefined,
      nextConfig: nextConfig
        ? {
            ...nextConfig,
            icon: (
              <Box display="flex" alignItems="center" gap={0.5}>
                <Box position="relative" display="inline-flex" alignItems="center">
                  <RightIcon />
                </Box>
                <Box position="absolute" top={nextConfig.type === 'table' ? 0 : -5} right={nextConfig.type === 'table' ? 0 : -5}>
                  <Tooltip
                    title={
                      <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                        <li>{translate('pages.design.document.infoIcon.point1')}</li>
                        <li>{translate('pages.design.document.infoIcon.point2')}</li>
                        <li>{translate('pages.design.document.infoIcon.point3')}</li>
                      </ul>
                    }
                    placement="top"
                  >
                    <InfoOutlinedIcon style={{ transform: 'scale(0.8)' }} />
                  </Tooltip>
                </Box>
              </Box>
            )
          }
        : undefined,
      uploadFileConfig: uploadFileConfig ? { ...uploadFileConfig, icon: <UploadFileIcon />, defaultTooltip: 'Upload File' } : undefined,
      tagFileConfig: tagFileConfig ? { ...tagFileConfig, icon: <SellOutlinedIcon />, defaultTooltip: 'Tag File' } : undefined,
      dpcConfig: dpcConfig ? { ...dpcConfig, icon: <ChecklistOutlinedIcon />, defaultTooltip: 'DataPointConfiguration' } : undefined,
      predictionConfig: predictionConfig ? { ...predictionConfig, icon: <TimelineIcon />, defaultTooltip: 'Prediction' } : undefined,
      lockResetConfig: lockResetConfig
        ? {
            ...lockResetConfig,
            icon: lockResetConfig.isLocked ? <LockOutlined color="error" /> : <LockOpenOutlined />,
            defaultTooltip: lockResetConfig.isLocked ? 'Unlock' : 'Lock'
          }
        : undefined,
      verifiedConfig: verifiedConfig ? { ...verifiedConfig, icon: <Verified color="success" />, defaultTooltip: 'Verified' } : undefined,
      notVerifiedConfig: notVerifiedConfig
        ? { ...notVerifiedConfig, icon: <NotVerifiedIcon color="error" />, defaultTooltip: 'Not Verified' }
        : undefined,
      tagging: tagging ? { ...tagging, icon: tagging.icon || <RightIcon />, defaultTooltip: 'Tagging' } : undefined,
      designStudio: designStudio
        ? { ...designStudio, icon: <LanOutlinedIcon className="designStudioIcon" />, defaultTooltip: 'Design Studio' }
        : undefined,
      refreshConfig: refreshConfig ? { ...refreshConfig, icon: <SyncIcon />, defaultTooltip: 'Refresh' } : undefined,
      resetConfig: resetConfig ? { ...resetConfig, icon: <RestartAltRoundedIcon />, defaultTooltip: 'Reset' } : undefined,
      clearConfig: clearConfig ? { ...clearConfig, icon: <BackspaceOutlinedIcon />, defaultTooltip: 'Clear' } : undefined,
      rightArrowConfig: rightArrowConfig
        ? { ...rightArrowConfig, icon: <ArrowCircleRightOutlinedIcon />, defaultTooltip: 'Go' }
        : undefined,
      viewConfig: viewConfig ? { ...viewConfig, icon: <RemoveRedEyeOutlinedIcon />, defaultTooltip: 'Go' } : undefined,
      lockConfig: lockConfig ? { ...lockConfig, icon: <LockOutlinedIcon />, defaultTooltip: 'Go' } : undefined,
      exportConfig: exportConfig
        ? {
            ...exportConfig,
            icon: <ExportDropdown onDownload={exportConfig.onDownload} disabled={exportConfig.disabled} />,
            defaultTooltip: 'Go'
          }
        : undefined
    };

    const isHavingIconButtons: boolean = useMemo(() => filter(iconOrder, (key) => buttonConfigs[key]).length > 0, [iconOrder]);

    return (
      <Stack
        direction="row"
        className="action-buttons"
        sx={{ whiteSpace: 'nowrap' }}
        alignItems="center"
        justifyContent={containerJustifyContent}
        display={containerDisplayProperty}
        ref={ref}
      >
        {isHavingIconButtons ? (
          <>
            {' '}
            {isMobile ? (
              <>
                <IconButton size="large" onClick={handleMenuClick}>
                  <MoreVertIcon />
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  {iconOrder.map((key) => {
                    const config = buttonConfigs[key];
                    if (config?.show) {
                      return (
                        <MenuItem key={key} onClick={config.onClick} disabled={config.disabled} className={config.className}>
                          <Box className="action-button-icon">{config.icon}</Box>
                          <Box ml={1}>{config.text || config.defaultTooltip}</Box>
                        </MenuItem>
                      );
                    }
                    return null;
                  })}
                </Menu>
              </>
            ) : (
              iconOrder.map((key) => {
                if (key === 'batchPredictionExportConfig' && batchPredictionExportConfig?.show) {
                  const { predictionReadyconfig, exportConfig } = batchPredictionExportConfig;

                  return (
                    <Box key={key} display="inline-flex" alignItems="center" gap={1}>
                      {/* Prediction */}

                      {predictionReadyconfig?.show && (
                        <IconButton
                          size="large"
                          onClick={predictionReadyconfig?.onClick}
                          disabled={predictionReadyconfig?.disabled}
                          sx={{ backgroundColor: theme.palette.background.paper }}
                        >
                          {predictionReadyconfig.showProgress ? (
                            <Box sx={{ position: 'relative', alignItems: 'center', justifyContent: 'center' }}>
                              <Box>
                                <Fade timeout={500} in={!predictionReadyconfig.toggleAnim}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {predictionReadyconfig.status === ReviewDocumentStatusType.QCINPROGRESS ||
                                      (predictionReadyconfig.status === ReviewDocumentStatusType.QCINTERRUPTED && <RefreshIcon />)}
                                    {!predictionReadyconfig?.disabled && <ArrowCircleRightOutlinedIcon />}
                                  </Box>
                                </Fade>
                              </Box>
                              {predictionReadyconfig?.disabled && (
                                <></>
                                // <Box sx={{ width: '100%' }} className="prediction-in-progress-linear-container">
                                //   <Fade timeout={500} in={predictionReadyconfig.toggleAnim}>
                                //     <LinearProgress variant="determinate" value={predictionReadyconfig.value} sx={{ height: 5, width: 80 }} />
                                //   </Fade>
                                // </Box>
                              )}
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {predictionReadyconfig.status === ReviewDocumentStatusType.QCINPROGRESS ||
                                (predictionReadyconfig.status === ReviewDocumentStatusType.QCINTERRUPTED && <RefreshIcon />)}
                              {!documentReadyConfig?.disabled && <ArrowCircleRightOutlinedIcon />}
                            </Box>
                          )}
                        </IconButton>
                      )}

                      {/* Export (layout-stable) */}
                      <Box
                        sx={{
                          width: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          visibility: exportConfig?.show ? 'visible' : 'hidden'
                        }}
                      >
                        {exportConfig && (
                          <ExportDropdown
                            onDownload={exportConfig.onDownload}
                            disabled={!exportConfig.show}
                            tooltip={exportConfig.tooltip}
                          />
                        )}
                      </Box>
                    </Box>
                  );
                }
                const config = buttonConfigs[key];
                if (config?.show) {
                  if (key === 'tagging' && tagging) {
                    return (
                      <Tooltip key={key} title={tagging.tooltip || 'Tagging'}>
                        <Button
                          variant={tagging.variant || 'outlined'}
                          startIcon={tagging.icon}
                          onClick={tagging.onClick}
                          disabled={tagging.disabled}
                          className={`action-button-tagging ${tagging.className || ''}`}
                        >
                          {tagging.text || ''}
                        </Button>
                      </Tooltip>
                    );
                  } else {
                    return (
                      <Tooltip key={key} title={config.tooltip || config.defaultTooltip || ''}>
                        <Box>
                          <IconButton
                            size="large"
                            onClick={config.onClick}
                            disabled={config.disabled}
                            className={config.className}
                            sx={{ backgroundColor: theme.palette.background.paper }}
                            {...(config.color ? { color: config.color } : {})}
                          >
                            {config.icon}
                          </IconButton>
                        </Box>
                      </Tooltip>
                    );
                  }
                }
                return null;
              })
            )}
          </>
        ) : (
          <></>
        )}

        {passwordResetConfig?.show || passwordCopyConfig?.show || inviteSendConfig?.show ? (
          <>
            <Divider orientation="vertical" flexItem sx={{ marginX: 1 }} />
            {passwordResetConfig?.show && !isMobile && (
              <>
                <Box component="span" className="action-password">
                  <Tooltip title={passwordResetConfig.tooltip || 'Reset Password'}>
                    <IconButton
                      size="large"
                      className={`${passwordResetConfig.className || ''}`}
                      onClick={passwordResetConfig.onClick}
                      disabled={passwordResetConfig.disabled}
                      sx={{ backgroundColor: theme.palette.background.paper }}
                    >
                      <SyncLockIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}
            {inviteSendConfig?.show && !isMobile && (
              <>
                <Box component="span" className="action-password">
                  <Tooltip title={inviteSendConfig.tooltip || 'Reset Password'}>
                    <IconButton
                      size="large"
                      className={`${inviteSendConfig.className || ''}`}
                      onClick={inviteSendConfig.onClick}
                      disabled={inviteSendConfig.disabled}
                      sx={{ backgroundColor: theme.palette.background.paper }}
                    >
                      <InviteSendArror />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}
            {passwordCopyConfig?.show && !isMobile && (
              <Tooltip title={isPasswordCopied ? 'Copied' : passwordCopyConfig?.tooltip || 'Copy Password'}>
                <IconButton
                  size="large"
                  onClick={_onPasswordCopy}
                  disabled={passwordCopyConfig?.disabled}
                  className={passwordCopyConfig?.className}
                  sx={{ backgroundColor: theme.palette.background.paper }}
                >
                  <CopyAllOutlined />
                </IconButton>
              </Tooltip>
            )}
          </>
        ) : (
          <></>
        )}

        {documentReadyConfig?.show && (
          <Button
            size="small"
            onClick={documentReadyConfig?.onClick}
            disabled={documentReadyConfig?.disabled}
            className={`my-5 documentReadyButton ${documentReadyConfig?.status} ${documentReadyConfig?.className} ${
              !documentReadyConfig?.disabled ? 'pr-5' : ''
            }`}
            variant={'contained'}
            color={'inherit'}
          >
            {documentReadyConfig.showProgress ? (
              <>
                {' '}
                <Box sx={{ position: 'relative', alignItems: 'center', justifyContent: 'center' }}>
                  <Box>
                    <Fade timeout={500} in={!documentReadyConfig.toggleAnim}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        {documentReadyButtonLangKeys[documentReadyConfig.status] ? (
                          <Typography style={{ textTransform: 'none' }}>
                            <FormattedMessage id={documentReadyButtonLangKeys[documentReadyConfig.status]} />
                          </Typography>
                        ) : (
                          documentReadyConfig.status
                        )}
                        {!documentReadyConfig?.disabled ? (
                          <span className="iconContainer" style={{ marginLeft: '30px' }}>
                            <RightArrow className="rightArrow" />
                          </span>
                        ) : (
                          <></>
                        )}
                      </Box>
                    </Fade>
                  </Box>
                  {documentReadyConfig.showProgress && documentReadyConfig?.disabled && (
                    <Box sx={{ width: '100%' }} className="prediction-in-progress-linear-container">
                      <Fade timeout={500} in={documentReadyConfig.toggleAnim}>
                        <LinearProgress variant="determinate" value={documentReadyConfig.value} sx={{ height: 5, width: 80 }} />
                      </Fade>
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <>
                {documentReadyButtonLangKeys[documentReadyConfig.status] ? (
                  <FormattedMessage id={documentReadyButtonLangKeys[documentReadyConfig.status]} />
                ) : (
                  documentReadyConfig.status
                )}
                {!documentReadyConfig?.disabled ? (
                  <span className="iconContainer ml-5">
                    <RightArrow className="rightArrow" />
                  </span>
                ) : (
                  <></>
                )}
              </>
            )}
          </Button>
        )}
        {/* {predictionReadyconfig?.show && (
          <IconButton
            size="large"
            onClick={predictionReadyconfig?.onClick}
            disabled={predictionReadyconfig?.disabled}
            sx={{ backgroundColor: theme.palette.background.paper }}
          >
            {predictionReadyconfig.showProgress ? (
              <Box sx={{ position: 'relative', alignItems: 'center', justifyContent: 'center' }}>
                <Box>
                  <Fade timeout={500} in={!predictionReadyconfig.toggleAnim}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {predictionReadyconfig.status === ReviewDocumentStatusType.QCINPROGRESS ||
                        (predictionReadyconfig.status === ReviewDocumentStatusType.QCINTERRUPTED && <RefreshIcon />)}
                      {!predictionReadyconfig?.disabled && <ArrowCircleRightOutlinedIcon />}
                    </Box>
                  </Fade>
                </Box>
                {predictionReadyconfig?.disabled && (
                  <></>
                  // <Box sx={{ width: '100%' }} className="prediction-in-progress-linear-container">
                  //   <Fade timeout={500} in={predictionReadyconfig.toggleAnim}>
                  //     <LinearProgress variant="determinate" value={predictionReadyconfig.value} sx={{ height: 5, width: 80 }} />
                  //   </Fade>
                  // </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {predictionReadyconfig.status === ReviewDocumentStatusType.QCINPROGRESS ||
                  (predictionReadyconfig.status === ReviewDocumentStatusType.QCINTERRUPTED && <RefreshIcon />)}
                {!documentReadyConfig?.disabled && <ArrowCircleRightOutlinedIcon />}
              </Box>
            )}
          </IconButton>
        )} */}
      </Stack>
    );
  }
);

export default ActionButtons;
